import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api } from '@/api/client';
import { formatDateTime } from '@/format';
import { useAuth } from '@/stores/auth';
import { confirmAction } from '@/stores/confirm';
import { toast } from '@/stores/toast';

interface Setup {
  uri: string;
  qrSvg: string;
  setupKey: string;
}

/**
 * Two-step sign-in for yourself: a 6-digit code from a phone app as well as
 * the password. Optional (.docs/019). Switching it on shows a QR code, asks
 * for the first code to prove the phone has it, then shows ten backup codes
 * once.
 */
export function TwoStep() {
  const queryClient = useQueryClient();
  const status = useQuery({
    queryKey: ['settings', 'two-step'],
    queryFn: () => api.get('/api/v1/settings/two-step'),
  });
  const [setup, setSetup] = useState<Setup | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['settings', 'two-step'] });
    void useAuth.getState().refreshUser().catch(() => undefined);
  };

  const start = useMutation({
    mutationFn: () => api.post<Setup>('/api/v1/settings/two-step/start'),
    onSuccess: (s) => {
      setSetup(s);
      setCode('');
      setError(null);
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Could not start.'),
  });

  const confirm = useMutation({
    mutationFn: (c: string) => api.post<{ codes: string[] }>('/api/v1/settings/two-step/confirm', { code: c }),
    onSuccess: (res) => {
      setSetup(null);
      setCodes(res.codes);
      refresh();
      toast.success('Two-step sign-in is on. Your other devices were signed out.');
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'That code is not right.'),
  });

  const newCodes = useMutation({
    mutationFn: () => api.post<{ codes: string[] }>('/api/v1/settings/two-step/new-codes'),
    onSuccess: (res) => {
      setCodes(res.codes);
      refresh();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Could not make new codes.'),
  });

  const off = useMutation({
    mutationFn: () => api.post('/api/v1/settings/two-step/off'),
    onSuccess: () => {
      setCodes(null);
      refresh();
      toast.success('Two-step sign-in is off.');
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Could not switch it off.'),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    confirm.mutate(code.trim());
  }

  const on = status.data?.enabled ?? false;

  return (
    <div className="settingsapp__block">
      <h3>Two-step sign-in</h3>
      <p className="settings__hint">
        Two-step sign-in asks for a 6-digit code from an app on your phone, as well as your
        password. Someone who learns your password still cannot sign in without your phone.
      </p>

      {status.isPending && <p className="muted">Loading…</p>}

      {codes && <BackupCodes codes={codes} onDone={() => setCodes(null)} />}

      {!codes && on && (
        <>
          <p>
            <span className="pill pill--ok">On</span>{' '}
            {status.data?.enabledAt ? `since ${formatDateTime(status.data.enabledAt)}. ` : ''}
            {status.data?.backupCodesLeft ?? 0} backup codes left.
          </p>
          <div className="settingsapp__blockrow">
            <button
              type="button"
              className="btn btn--sm"
              disabled={newCodes.isPending}
              onClick={() =>
                void confirmAction({
                  title: 'Make new backup codes?',
                  message: 'Your old backup codes will stop working.',
                  confirmLabel: 'Make new codes',
                }).then((ok) => ok && newCodes.mutate())
              }
            >
              New backup codes
            </button>
            <button
              type="button"
              className="btn btn--sm btn--danger"
              disabled={off.isPending}
              onClick={() =>
                void confirmAction({
                  title: 'Switch off two-step sign-in?',
                  message: 'Signing in will need only your password again.',
                  confirmLabel: 'Switch it off',
                  danger: true,
                }).then((ok) => ok && off.mutate())
              }
            >
              Switch off
            </button>
          </div>
        </>
      )}

      {!codes && !on && !setup && status.data && (
        <div className="settingsapp__blockrow">
          <button
            type="button"
            className="btn btn--primary btn--sm"
            disabled={start.isPending}
            onClick={() => start.mutate()}
          >
            Switch on two-step sign-in
          </button>
        </div>
      )}

      {setup && (
        <form className="twostep" onSubmit={submit}>
          <ol className="twostep__steps">
            <li>
              Install an authenticator app on your phone, such as Google Authenticator or
              Microsoft Authenticator.
            </li>
            <li>
              In the app, add an account and scan this code.
              <img
                className="twostep__qr"
                alt="QR code to scan with the authenticator app"
                src={`data:image/svg+xml;utf8,${encodeURIComponent(setup.qrSvg)}`}
              />
              Cannot scan? Type this key into the app instead:{' '}
              <span className="mono mono--ascase">{setup.setupKey}</span>
            </li>
            <li>
              <label className="field">
                <span className="field__label">Type the 6-digit code the app shows</span>
                <input
                  className="field__input login__code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123456"
                  required
                />
              </label>
            </li>
          </ol>
          {error && (
            <p className="errornote" role="alert">
              <span aria-hidden="true">⚠ </span>
              {error}
            </p>
          )}
          <div className="settingsapp__blockrow">
            <button type="button" className="btn btn--sm" onClick={() => setSetup(null)}>
              Cancel
            </button>
            <button type="submit" className="btn btn--primary btn--sm" disabled={confirm.isPending}>
              {confirm.isPending ? 'Checking…' : 'Switch on'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

/** Shown once. Each code works one time, for when the phone is not to hand. */
function BackupCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const [copied, setCopied] = useState(false);
  const text = codes.join('\n');

  function print() {
    const win = window.open('', '_blank', 'width=420,height=560');
    if (!win) return;
    win.document.title = 'Backup codes';
    const body = win.document.body;
    const h = win.document.createElement('h1');
    h.textContent = 'Two-step backup codes';
    const p = win.document.createElement('p');
    p.textContent = 'Each code works once. Keep this somewhere safe, away from your phone.';
    const pre = win.document.createElement('pre');
    pre.textContent = text;
    pre.style.fontSize = '18px';
    body.append(h, p, pre);
    win.print();
  }

  return (
    <div className="twostep__codes">
      <p className="login__notice" role="status">
        <span aria-hidden="true">ⓘ </span>
        These are shown once. Keep them somewhere safe, away from your phone. Each works one time,
        if your phone is lost or not to hand.
      </p>
      <ul className="twostep__list">
        {codes.map((c) => (
          <li key={c} className="mono mono--ascase">
            {c}
          </li>
        ))}
      </ul>
      <div className="settingsapp__blockrow">
        <button
          type="button"
          className="btn btn--sm"
          onClick={() =>
            void navigator.clipboard?.writeText(text).then(() => setCopied(true), () => undefined)
          }
        >
          {copied ? '✓ Copied' : 'Copy'}
        </button>
        <button type="button" className="btn btn--sm" onClick={print}>
          Print
        </button>
        <button type="button" className="btn btn--primary btn--sm" onClick={onDone}>
          I have kept them
        </button>
      </div>
    </div>
  );
}
